import * as Sentry from '@sentry/react';
import { normalizeApiPath, sanitizeApiUrl } from './sanitizeUrl';

const MAX_ENVELOPE_JSON_BYTES = 2048;

// Expected outcomes, not defects (~80% of Sentry issues before this filter): 0 no response,
// 401 session expiry (interceptor redirects), 409 conflicts handled inline, 429 rate limits.
const EXPECTED_STATUSES: ReadonlySet<number> = new Set([0, 401, 409, 429]);

interface AxiosErrorShape {
    response?: {
        status?: number;
        statusText?: string;
        data?: unknown;
    };
    config?: {
        method?: string;
        url?: string;
    };
    code?: string;
}

function extractBackendErrorCode(data: unknown): string | undefined {
    if (typeof data !== 'object' || data === null) return undefined;
    const envelope = data as Record<string, unknown>;
    const errorField = envelope['error'];
    if (typeof errorField !== 'object' || errorField === null) return undefined;
    const code = (errorField as Record<string, unknown>)['code'];
    if (typeof code !== 'string' || code.length === 0) return undefined;
    return code;
}

function extractBackendErrorEnvelope(data: unknown): Record<string, unknown> | undefined {
    if (typeof data !== 'object' || data === null) return undefined;
    const errorField = (data as Record<string, unknown>)['error'];
    if (typeof errorField !== 'object' || errorField === null) return undefined;
    // Guard against unexpectedly large payloads before attaching to Sentry.
    const serialized = JSON.stringify(errorField);
    if (serialized.length > MAX_ENVELOPE_JSON_BYTES) return undefined;
    return errorField as Record<string, unknown>;
}

// Never attaches bodies, headers or cookies; the backend error envelope is the one exception
// because the backend guarantees API error responses carry no patient PII.
export function captureApiError(error: unknown): void {
    // An error reporter must never throw itself - `?.` guards properties but
    // not a null/undefined receiver, so normalise the receiver first.
    const axiosError = (error ?? {}) as AxiosErrorShape;
    const status = axiosError.response?.status ?? 0;
    if (EXPECTED_STATUSES.has(status)) return;

    const method = axiosError.config?.method?.toUpperCase() ?? 'UNKNOWN';
    const sanitizedPath = sanitizeApiUrl(axiosError.config?.url);
    // Grouping key: ids collapsed so one failing endpoint = one issue. The raw
    // path (minus query) stays in `http.url` for per-event debugging.
    const normalizedPath = normalizeApiPath(sanitizedPath);
    const statusText = axiosError.response?.statusText ?? '';
    const axiosCode = axiosError.code ?? '';
    const responseData = axiosError.response?.data;
    const backendCode = extractBackendErrorCode(responseData);
    const backendEnvelope = extractBackendErrorEnvelope(responseData);

    const endpoint = method !== 'UNKNOWN' ? `${method} ${normalizedPath}` : normalizedPath;

    Sentry.withScope((scope) => {
        scope.setTag('http.method', method);
        scope.setTag('http.status_code', String(status));
        scope.setTag('http.url', sanitizedPath);
        scope.setTag('http.status_text', statusText);
        scope.setTag('error.code', axiosCode);
        scope.setTag('endpoint', endpoint);

        if (backendCode !== undefined) {
            scope.setTag('backend.error_code', backendCode);
        }

        if (backendEnvelope !== undefined) {
            scope.setContext('backend_error', backendEnvelope);
        }

        scope.setFingerprint(['api-error', method, normalizedPath, String(status)]);

        Sentry.captureException(error);
    });
}
